import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Play, Pause } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useOnboarding } from '../hooks/useOnboarding';

const HeroSection = () => {
    // Configuration du temps de delay pour les images (en millisecondes)
    const IMAGE_DELAY_TIME = 5000; // 5 secondes

    const [currentSlide, setCurrentSlide] = useState(0);
    const [isVideoPaused, setIsVideoPaused] = useState(false);
    const { t, currentLanguage } = useLanguage();
    const { items: onboardingItems, loading, error } = useOnboarding(currentLanguage);

    // Fallback slides en cas d'erreur ou de chargement


    // Debug: Log des données récupérées
    useEffect(() => {
        console.log('=== ONBOARDING DEBUG ===');
        console.log('Items:', onboardingItems);
        console.log('Loading:', loading);
        console.log('Error:', error);
        console.log('Language:', currentLanguage);

        // Debug spécifique pour les types de fichiers
        onboardingItems.forEach((item, index) => {
            console.log(`Item ${index}:`, {
                id: item.id,
                title: item.title,
                file_type: item.file_type,
                file_type_typeof: typeof item.file_type,
                file_url: item.file_url,
                is_video: item.file_type === 'video'
            });
        });
    }, [onboardingItems, loading, error, currentLanguage]);

    // Utiliser les données d'onboarding ou les slides de fallback
    const slides = onboardingItems.length > 0
        ? onboardingItems.sort((a, b) => a.sort_order - b.sort_order)
        : [];

    // Auto-slide logic
    useEffect(() => {
        if (slides.length <= 1) return;

        let timer: ReturnType<typeof setTimeout> | null = null;
        const currentSlideData = slides[currentSlide];

        // Debug du slide actuel
        console.log('=== CURRENT SLIDE DEBUG ===');
        console.log('Current slide index:', currentSlide);
        console.log('Current slide data:', currentSlideData);
        console.log('File type:', currentSlideData?.file_type);
        console.log('Is video?', isVideoFile(currentSlideData?.file_type));
        console.log('File URL:', currentSlideData?.file_url);
        console.log('Generated URL:', currentSlideData ? getFileUrl(currentSlideData.file_url) : 'N/A');

        const nextSlideAfterDelay = () => {
            setCurrentSlide((prev) => (prev + 1) % slides.length);
        };

        if (isVideoFile(currentSlideData.file_type) && !isVideoPaused) {
            // Pour les vidéos, la transition se fait via l'événement onEnded
            // On ne programme pas de timer automatique
        } else {
            // Pour les images, transition automatique après le délai configuré
            timer = setTimeout(nextSlideAfterDelay, IMAGE_DELAY_TIME);
        }

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [currentSlide, slides.length, isVideoPaused, IMAGE_DELAY_TIME]);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % slides.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
    };

    const handleVideoEnded = () => {
        nextSlide();
    };

    const toggleVideoPause = () => {
        setIsVideoPaused(!isVideoPaused);
        const video = document.querySelector('video') as HTMLVideoElement;
        if (video) {
            if (isVideoPaused) {
                video.play();
            } else {
                video.pause();
            }
        }
    };

    const getFileUrl = (fileUrl: string) => {
        if (!fileUrl) {
            console.warn('File URL is empty or undefined');
            return '';
        }

        if (fileUrl.startsWith('http')) {
            console.log('Using absolute URL:', fileUrl);
            return fileUrl;
        }

        // Construire l'URL complète pour les fichiers stockés
        const baseUrl = 'http://localhost:2030';
        const fullUrl = `${baseUrl}/uploads/${fileUrl}`;
        console.log('Constructed URL:', fullUrl);
        return fullUrl;
    };

    const isVideoFile = (fileType: string) => {
        if (!fileType) {
            console.warn('File type is empty or undefined');
            return false;
        }
        const normalizedType = fileType.toString().trim().toLowerCase();
        console.log('Checking file type:', normalizedType, 'is video?', normalizedType === 'video');
        return normalizedType === 'video';
    };

    if (loading) {
        return (
            <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-amber-900/20 to-slate-900 overflow-hidden">
                {/* Background animated particles */}
                <div className="absolute inset-0">
                    <div className="absolute top-1/4 left-1/4 w-72 h-72 bg-amber-500/10 rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl animate-pulse delay-1000"></div>
                </div>

                <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8 }}
                    className="text-center text-white z-10"
                >
                    {/* Logo/Brand placeholder */}
                    <motion.div
                        initial={{ y: -20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.2 }}
                        className="mb-8"
                    >
                        <h1 className="text-4xl md:text-5xl font-bold text-transparent bg-gradient-to-r from-amber-400 to-orange-500 bg-clip-text mb-2">
                            restaurant
                        </h1>
                        <p className="text-amber-200/80 text-lg">Restaurant & Bar</p>
                    </motion.div>

                    {/* Enhanced loading spinner */}
                    <motion.div
                        initial={{ scale: 0 }}
                        animate={{ scale: 1 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="relative mb-6"
                    >
                        <div className="w-20 h-20 mx-auto relative">
                            <div className="absolute inset-0 border-4 border-amber-500/30 rounded-full"></div>
                            <div className="absolute inset-0 border-4 border-transparent border-t-amber-500 rounded-full animate-spin"></div>
                            <div className="absolute inset-2 border-2 border-transparent border-t-orange-400 rounded-full animate-spin animate-reverse"></div>
                        </div>
                    </motion.div>

                    <motion.p
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                        className="text-xl text-amber-100/90 font-light tracking-wide"
                    >
                        {t('loading', 'Chargement...')}
                    </motion.p>

                    {/* Loading progress bar */}
                    <motion.div
                        initial={{ width: 0 }}
                        animate={{ width: "100%" }}
                        transition={{ duration: 2, delay: 0.8, repeat: Infinity }}
                        className="h-1 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full mx-auto mt-4 max-w-xs"
                    />
                </motion.div>
            </section>
        );
    }

    if (error || slides.length === 0) {
        return (
            <section className="relative h-screen flex items-center justify-center bg-gradient-to-br from-slate-900 via-red-900/20 to-slate-900 overflow-hidden">
                {/* Background animated particles */}
                <div className="absolute inset-0">
                    <div className="absolute top-1/3 left-1/3 w-80 h-80 bg-red-500/10 rounded-full blur-3xl animate-pulse"></div>
                    <div className="absolute bottom-1/3 right-1/3 w-64 h-64 bg-orange-500/10 rounded-full blur-3xl animate-pulse delay-700"></div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                    className="text-center text-white z-10 max-w-2xl mx-auto px-4"
                >
                    {/* Error icon */}
                    <motion.div
                        initial={{ scale: 0, rotate: -180 }}
                        animate={{ scale: 1, rotate: 0 }}
                        transition={{ duration: 0.8, delay: 0.2, type: "spring", stiffness: 100 }}
                        className="mb-8"
                    >
                        <div className="w-24 h-24 mx-auto bg-gradient-to-br from-red-500/20 to-orange-500/20 rounded-full border-2 border-red-400/30 flex items-center justify-center backdrop-blur-sm">
                            <div className="text-5xl">😔</div>
                        </div>
                    </motion.div>

                    {/* Error title */}
                    <motion.h2
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.4 }}
                        className="text-4xl md:text-5xl font-bold mb-4 text-transparent bg-gradient-to-r from-red-400 to-orange-500 bg-clip-text"
                    >
                        {t('error.title', 'Oops!')}
                    </motion.h2>

                    {/* Error message */}
                    <motion.p
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.6 }}
                        className="text-xl text-gray-300 mb-8 leading-relaxed"
                    >
                        {t('error.hero', 'Impossible de charger le contenu')}
                    </motion.p>

                    {/* Retry button */}
                    <motion.button
                        initial={{ y: 20, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 0.6, delay: 0.8 }}
                        onClick={() => window.location.reload()}
                        className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-semibold rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl backdrop-blur-sm border border-amber-500/20"
                    >
                        <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Réessayer
                    </motion.button>
                </motion.div>
            </section>
        );
    }

    const currentSlideData = slides[currentSlide];

    return (
        <section className="relative h-screen overflow-hidden bg-slate-950">
            {/* Background container with enhanced transitions */}
            <AnimatePresence mode="wait">
                <motion.div
                    key={currentSlide}
                    initial={{ opacity: 0, scale: 1.1 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ duration: 1.2, ease: "easeInOut" }}
                    className="absolute inset-0"
                >
                    {(() => {
                        const isVideo = isVideoFile(currentSlideData.file_type);

                        if (isVideo) {
                            return (
                                <video
                                    className="absolute inset-0 w-full h-full object-cover transform scale-105"
                                    src={getFileUrl(currentSlideData.file_url)}
                                    autoPlay
                                    muted
                                    playsInline
                                    onEnded={handleVideoEnded}
                                    onError={(e) => {
                                        console.error('Erreur de lecture vidéo:', e);
                                        nextSlide();
                                    }}
                                    onLoadStart={() => console.log('Video load started')}
                                    onCanPlay={() => console.log('Video can play')}
                                />
                            );
                        } else {
                            return (
                                <motion.img
                                    initial={{ scale: 1.1 }}
                                    animate={{ scale: 1 }}
                                    transition={{ duration: 8, ease: "easeOut" }}
                                    className="absolute inset-0 w-full h-full object-cover"
                                    src={getFileUrl(currentSlideData.file_url)}
                                    alt={currentSlideData.title}
                                    onError={(e) => {
                                        console.error('Erreur de chargement image:', e);
                                        const target = e.target as HTMLImageElement;
                                        target.src = '/images/hero-2.jpg';
                                    }}
                                />
                            );
                        }
                    })()}

                    {/* Enhanced overlay with gradient and blur effects */}
                    <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/70" />
                    <div className="absolute inset-0 bg-gradient-to-r from-amber-900/30 via-transparent to-orange-900/30" />

                    {/* Animated particles overlay */}
                    <div className="absolute inset-0 opacity-30">
                        <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-amber-400 rounded-full animate-pulse blur-sm"></div>
                        <div className="absolute top-3/4 right-1/3 w-1 h-1 bg-orange-400 rounded-full animate-pulse delay-1000 blur-sm"></div>
                        <div className="absolute bottom-1/3 left-1/2 w-1.5 h-1.5 bg-amber-300 rounded-full animate-pulse delay-500 blur-sm"></div>
                        <div className="absolute top-1/2 right-1/4 w-1 h-1 bg-orange-300 rounded-full animate-pulse delay-1500 blur-sm"></div>
                    </div>
                </motion.div>
            </AnimatePresence>

            {/* Contenu du slide avec design moderne */}
            <div className="relative z-10 h-full flex items-center justify-center">
                <div className="container mx-auto px-6 lg:px-8 text-center text-white">
                    <motion.div
                        key={`content-${currentSlide}`}
                        initial={{ y: 60, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ duration: 1, delay: 0.3, ease: "easeOut" }}
                        className="max-w-5xl mx-auto"
                    >
                        {/* Tag moderne avec effet glassmorphism */}
                        <motion.div
                            initial={{ scale: 0.8, opacity: 0, y: -20 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.5, type: "spring", stiffness: 100 }}
                            className="mb-8"
                        >
                            <span className="inline-flex items-center px-6 py-3 bg-white/10 backdrop-blur-md border border-amber-400/40 rounded-full text-amber-200 text-sm font-semibold tracking-wider uppercase shadow-lg">
                                <div className="w-2 h-2 bg-amber-400 rounded-full mr-3 animate-pulse"></div>
                                {currentSlideData.tag}
                            </span>
                        </motion.div>

                        {/* Titre avec effet de révélation */}
                        <motion.div
                            className="mb-8 overflow-hidden"
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 1, delay: 0.7 }}
                        >
                            <h1 className="text-5xl md:text-7xl lg:text-8xl font-bold leading-none">
                                <span className="block text-transparent bg-gradient-to-r from-white via-amber-100 to-orange-200 bg-clip-text drop-shadow-2xl">
                                    {currentSlideData.title}
                                </span>
                            </h1>
                        </motion.div>

                        {/* Description avec typographie améliorée */}
                        {currentSlideData.description && (
                            <motion.div
                                initial={{ y: 30, opacity: 0 }}
                                animate={{ y: 0, opacity: 1 }}
                                transition={{ duration: 0.8, delay: 0.9 }}
                                className="mb-12"
                            >
                                <p className="text-xl md:text-2xl lg:text-3xl mb-6 text-gray-100/90 max-w-4xl mx-auto leading-relaxed font-light">
                                    {currentSlideData.description}
                                </p>
                                <div className="w-24 h-1 bg-gradient-to-r from-amber-500 to-orange-500 mx-auto rounded-full"></div>
                            </motion.div>
                        )}

                        {/* Boutons d'action redessinés */}
                        <motion.div
                            initial={{ y: 30, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            transition={{ duration: 0.8, delay: 1.1 }}
                            className="flex flex-col sm:flex-row gap-6 justify-center items-center"
                        >
                            <Link
                                to="/reservation"
                                className="group relative inline-flex items-center px-10 py-5 bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-white font-bold rounded-2xl transition-all duration-500 transform hover:scale-105 shadow-2xl hover:shadow-amber-500/25 border border-amber-500/20 backdrop-blur-sm overflow-hidden"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-white/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <svg className="w-5 h-5 mr-3 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                                <span className="relative">{t('hero.cta.reservation', 'Réserver une table')}</span>
                            </Link>

                            <Link
                                to="/menu"
                                className="group relative inline-flex items-center px-10 py-5 bg-white/10 hover:bg-white/20 border-2 border-white/30 hover:border-white/60 text-white font-bold rounded-2xl transition-all duration-500 backdrop-blur-md shadow-xl hover:shadow-2xl transform hover:scale-105 overflow-hidden"
                            >
                                <div className="absolute inset-0 bg-gradient-to-r from-amber-500/10 to-orange-500/10 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                <svg className="w-5 h-5 mr-3 group-hover:rotate-12 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <span className="relative">{t('hero.cta.menu', 'Voir le menu')}</span>
                            </Link>
                        </motion.div>
                    </motion.div>
                </div>
            </div>

            {/* Contrôles de navigation redessinés */}
            {slides.length > 1 && (
                <>
                    {/* Boutons précédent/suivant avec design moderne */}
                    <motion.button
                        onClick={prevSlide}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 1.5 }}
                        whileHover={{ scale: 1.1, x: -5 }}
                        whileTap={{ scale: 0.95 }}
                        className="group absolute left-6 top-1/2 transform -translate-y-1/2 z-20 p-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white hover:bg-white/20 transition-all duration-500 shadow-xl hover:shadow-2xl"
                        aria-label={t('aria.previousSlide')}
                    >
                        <ChevronLeft className="w-6 h-6 group-hover:-translate-x-1 transition-transform duration-300" />
                        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </motion.button>

                    <motion.button
                        onClick={nextSlide}
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.8, delay: 1.5 }}
                        whileHover={{ scale: 1.1, x: 5 }}
                        whileTap={{ scale: 0.95 }}
                        className="group absolute right-6 top-1/2 transform -translate-y-1/2 z-20 p-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white hover:bg-white/20 transition-all duration-500 shadow-xl hover:shadow-2xl"
                        aria-label={t('aria.nextSlide')}
                    >
                        <ChevronRight className="w-6 h-6 group-hover:translate-x-1 transition-transform duration-300" />
                        <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </motion.button>

                    {/* Contrôle vidéo moderne */}
                    {isVideoFile(currentSlideData.file_type) && (
                        <motion.button
                            onClick={toggleVideoPause}
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 0.8, delay: 1.7, type: "spring", stiffness: 100 }}
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.95 }}
                            className="group absolute bottom-24 right-6 z-20 p-4 bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl text-white hover:bg-white/20 transition-all duration-500 shadow-xl hover:shadow-2xl"
                            aria-label={isVideoPaused ? t('aria.resumeVideo') : t('aria.pauseVideo')}
                        >
                            {isVideoPaused ?
                                <Play className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" /> :
                                <Pause className="w-6 h-6 group-hover:scale-110 transition-transform duration-300" />
                            }
                            <div className="absolute inset-0 bg-gradient-to-r from-amber-500/20 to-orange-500/20 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                        </motion.button>
                    )}

                    {/* Indicateurs de slides modernes */}
                    <motion.div
                        className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, delay: 1.3 }}
                    >
                        <div className="flex space-x-3 bg-black/20 backdrop-blur-md rounded-full px-4 py-2 border border-white/20">
                            {slides.map((_, index) => (
                                <motion.button
                                    key={index}
                                    onClick={() => setCurrentSlide(index)}
                                    whileHover={{ scale: 1.2 }}
                                    whileTap={{ scale: 0.9 }}
                                    className={`relative transition-all duration-500 ${index === currentSlide
                                        ? 'w-8 h-3 bg-gradient-to-r from-amber-500 to-orange-500 rounded-full'
                                        : 'w-3 h-3 bg-white/40 hover:bg-white/60 rounded-full'
                                        }`}
                                    aria-label={t('aria.goToSlide', index + 1)}
                                >
                                    {index === currentSlide && (
                                        <motion.div
                                            layoutId="activeSlide"
                                            className="absolute inset-0 bg-gradient-to-r from-amber-400 to-orange-400 rounded-full shadow-lg"
                                            transition={{ type: "spring", stiffness: 300, damping: 30 }}
                                        />
                                    )}
                                </motion.button>
                            ))}
                        </div>
                    </motion.div>
                </>
            )}


        </section>
    );
};

export default HeroSection;